import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import clsx from 'clsx';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { TimeProgress } from '@/App/tiles/shared/TimeProgress';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton/index.tsx';
import { useCancelFlexiForwardRfsMutation } from '@/store/flexi-forward/api';
import { getFlexiForwardQuote } from '@/store/flexi-forward/helpers/rfs.ts';
import { selectFlexiForwardRfsStatus } from '@/store/flexi-forward/selectors/rfs';
import { requestFlexiForwardRfsThunk } from '@/store/flexi-forward/thunks.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

export function FlexiForwardPriceButton({ tileId }: { tileId: string }) {
  const isDefinedFlexiForward = useFlexiForwardSelector((flexiForward) => isDefined(flexiForward));

  return !isDefinedFlexiForward ? null : (
    <div className="d-flex flex-column align-items-center mb-8px">
      <FlexiForwardPriceButtonFromRfsStatus tileId={tileId} />
    </div>
  );
}

function FlexiForwardPriceButtonFromRfsStatus({ tileId }: { tileId: string }) {
  const rfsStatus = useFlexiForwardSelector((flexiForward) => selectFlexiForwardRfsStatus(flexiForward));

  switch (rfsStatus) {
    case 'NOT_PRICEABLE':
      return <NotPriceableButton pricingType="Indicative" />;
    case 'PRICEABLE':
      return <Priceable tileId={tileId} pricingType="Indicative" forceTradeable={false} />;
    case 'AWAITING_INDICATIVE':
      return <ShortcutExecutable tileId={tileId} />;
    case 'AWAITING_EXECUTABLE':
      return <AwaitingExecutable tileId={tileId} />;
    case 'STREAMING_INDICATIVE':
      return <Priceable tileId={tileId} pricingType="Tradeable" forceTradeable={true} />;
    case 'STREAMING_EXECUTABLE':
      return <Progress tileId={tileId} />;
    case 'ERROR':
      return <Priceable tileId={tileId} pricingType="Indicative" forceTradeable={false} />;
    case 'BYPASSABLE_ERROR':
      return <span>Bypassable error</span>;
    // case 'BYPASSED_CHECK_PRICING':
    //   return <span>Bypassable check pricing</span>;
    default:
      return assertUnreachable(rfsStatus, `Unhandled price stream status: ${rfsStatus}`);
  }
}

function NotPriceableButton({ pricingType }: { pricingType: 'Tradeable' | 'Indicative' }) {
  return (
    <button
      data-e2e={`price-button-${pricingType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className="btn btn-info btn-sm btn-block"
      disabled={true}
    >
      <FormattedMessage id={`request.${pricingType}.price`} />
    </button>
  );
}

function Priceable({
  tileId,
  pricingType,
  forceTradeable,
}: {
  tileId: string;
  pricingType: 'Tradeable' | 'Indicative';
  forceTradeable: boolean;
}) {
  const dispatch = useAppDispatch();
  const clientsWay = useFlexiForwardSelector((forwardAccumulator) => {
    return forwardAccumulator.properties.clientsWay;
  });
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  return (
    <button
      data-e2e={`price-button-${pricingType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className={clsx('btn btn-sm btn-block', {
        'btn-sell': showColoredPrices && clientsWay === 'Sell',
        'btn-buy': showColoredPrices && clientsWay === 'Buy',
        'btn-info': !showColoredPrices,
      })}
      onClick={() => {
        dispatch(requestFlexiForwardRfsThunk({ tileId, forceTradeable }));
      }}
    >
      <FormattedMessage id={`request.${pricingType}.price`} />
    </button>
  );
}

function ShortcutExecutable({ tileId }: { tileId: string }) {
  const dispatch = useAppDispatch();
  const [cancelFlexiForwardRfs] = useCancelFlexiForwardRfsMutation();
  const rfsId = useFlexiForwardSelector((flexiForward) => {
    const rfsState = flexiForward.properties.rfs;

    return isDefined(rfsState) && 'rfsId' in rfsState ? rfsState.rfsId : undefined;
  });
  const clientsWay = useFlexiForwardSelector((forwardAccumulator) => {
    return forwardAccumulator.properties.clientsWay;
  });
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const bypassCheck = false;

  const byPassIndicative = () => {
    dispatch(requestFlexiForwardRfsThunk({ tileId, forceTradeable: true }));
  };

  const cancelIndicative = useCallback(() => {
    if (isNotDefined(rfsId)) {
      return;
    }
    cancelFlexiForwardRfs({
      tileId,
      rfsId,
    });
  }, [tileId, cancelFlexiForwardRfs, rfsId]);

  return (
    <div className="progress w-100" style={{ height: '20px' }}>
      <div
        className={`progress-bar progress-bar-striped progress-bar-animated ${showColoredPrices ? 'rate-colored-prices' : 'bg-info'} ${clientsWay === 'Buy' ? 'buy' : 'sell'} w-100`}
        role="progressbar"
      />
      <SgButton
        type="button"
        purpose="discreet"
        size="sm"
        className="btn btn-sm btn-flat-primary flex-grow-0"
        onClick={cancelIndicative}
      >
        <i className="icon icon-sm">close</i>
      </SgButton>
      <button
        type="button"
        className={`btn btn-sm btn-icon btn-flat-${bypassCheck ? 'warning' : 'info'}`}
        onClick={byPassIndicative}
        data-e2e={'skip-to-executable-price-button'}
      >
        <i className="icon">skip_next</i>
      </button>
    </div>
  );
}

function AwaitingExecutable({ tileId }: { tileId: string }) {
  const [cancelFlexiForwardRfs] = useCancelFlexiForwardRfsMutation();
  const clientsWay = useFlexiForwardSelector((forwardAccumulator) => {
    return forwardAccumulator.properties.clientsWay;
  });
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const rfsId = useFlexiForwardSelector((flexiForward) => {
    const rfsState = flexiForward.properties.rfs;

    return isDefined(rfsState) && 'rfsId' in rfsState ? rfsState.rfsId : undefined;
  });

  const handleCancelRequest = useCallback(() => {
    if (isNotDefined(rfsId)) {
      return;
    }

    cancelFlexiForwardRfs({
      tileId,
      rfsId,
    });
  }, [rfsId, tileId, cancelFlexiForwardRfs]);

  return (
    <div className="progress w-100" style={{ height: '20px' }}>
      <div
        className={`progress-bar progress-bar-striped progress-bar-animated ${showColoredPrices ? 'rate-colored-prices' : 'bg-info'} ${clientsWay === 'Buy' ? 'buy' : 'sell'} w-100`}
        role="progressbar"
      />
      <button
        type="button"
        className="btn btn-sm btn-flat-primary"
        data-e2e="cancel-rfs-button"
        onClick={handleCancelRequest}
      >
        <i className="icon">close</i>
      </button>
    </div>
  );
}

function Progress({ tileId }: { tileId: string }) {
  const [cancelFlexiForwardRfs] = useCancelFlexiForwardRfsMutation();

  const { totalTime, remainingTime, rfsId, clientsWay } = useFlexiForwardSelector((flexiForward) => {
    const { properties } = flexiForward;

    const quote = getFlexiForwardQuote(properties.rfs);
    const rfsWindow = quote?.rfsWindow;
    const totalTime =
      isDefined(properties.rfs) && properties.rfs.status === 'PRICING' ? properties.rfs.initialRfsWindow : undefined;

    const rfsState = properties.rfs;
    const rfsId = isDefined(rfsState) && 'rfsId' in rfsState ? rfsState.rfsId : undefined;

    return {
      totalTime,
      remainingTime: rfsWindow,
      rfsId,
      clientsWay: properties.clientsWay,
    };
  });

  const handleCancelRequest = useCallback(() => {
    if (isNotDefined(rfsId)) {
      return;
    }

    cancelFlexiForwardRfs({
      tileId,
      rfsId,
    });
  }, [rfsId, tileId, cancelFlexiForwardRfs]);

  return (
    <TimeProgress
      totalTime={totalTime}
      remainingTime={remainingTime}
      onCancel={handleCancelRequest}
      data-e2e="rfs"
      clientsWay={clientsWay}
    />
  );
}
